import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FiSave, FiRotateCcw, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { createTalent } from '../../../../store/talents/talentsSlice';

const Page = styled.div`
  padding: 28px 24px;
  max-width: 760px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 28px 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block; font-size: 12px; font-weight: 600; color: #9ca3af;
    margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, textarea {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 10px 13px; color: #e2e8f0; font-size: 13.5px;
    font-family: inherit; outline: none; transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); }
  }
  textarea { resize: vertical; min-height: 120px; }
  .err { font-size: 12px; color: #f87171; margin-top: 5px; }
  .hint { font-size: 11.5px; color: #6b7280; margin-top: 5px; }
`;

const BtnRow = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.55 : 1};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 20px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; }
`;

const InfoBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 24px;
  svg { color: #fbbf24; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fde68a; line-height: 1.5; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 48px 24px; text-align: center;
  svg { font-size: 52px; color: #34d399; }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 14px; color: #6b7280; max-width: 360px; }
`;

const AddTalentPage = ({ category, icon, titleKey, subKey, listPath, descriptionLabel, descriptionPlaceholder }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const talents = useSelector(state => state.talents);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  if (!auth.isAuth) return <Navigate to="/login" />;

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append('category', category);
    fd.append('description', values.description);
    fd.append('discord', values.discord || '');
    if (values.images?.length) {
      Array.from(values.images).forEach(file => fd.append('images', file));
    }
    dispatch(createTalent(fd)).unwrap().then(() => {
      setSubmitted(true);
    });
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon">{icon}</div>
          <div>
            <div className="title">{t(titleKey)}</div>
            <div className="sub">{t(subKey)}</div>
          </div>
        </PageHeader>

        <Card>
          {submitted ? (
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.talents.success_title', 'تم إرسال طلبك بنجاح!')}</div>
              <div className="sub">{t('website.talents.success_sub', 'طلبك قيد المراجعة، سيظهر بعد موافقة الإدارة.')}</div>
              <Btn onClick={() => navigate(listPath)} style={{ marginTop: 8 }}>
                {t('website.talents.go_list', 'عرض القائمة')}
              </Btn>
            </SuccessBox>
          ) : (
            <>
              <InfoBanner>
                <FiAlertCircle />
                <div className="msg">{t('website.talents.pending_note', 'طلبك سيظهر في القائمة بعد مراجعة الإدارة والموافقة عليه.')}</div>
              </InfoBanner>

              {talents.errors.length > 0 && (
                <AlertBox>
                  <FiAlertCircle />
                  <div className="msg">{talents.errors.map((e, i) => <div key={i}>{e?.msg}</div>)}</div>
                </AlertBox>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <label>{descriptionLabel || t('website.talents.description', 'وصف شغلك')}</label>
                  <textarea placeholder={descriptionPlaceholder || t('website.talents.description_placeholder', 'اكتب وصفاً عن شغلك وخبرتك...')} {...register('description', { required: t('validation.required', 'مطلوب') })} />
                  {errors.description && <div className="err">{errors.description.message}</div>}
                </FormGroup>

                <FormGroup>
                  <label>{t('website.talents.discord', 'ديسكورد')}</label>
                  <input placeholder={t('website.talents.discord_placeholder', 'اسم المستخدم أو رابط الدعوة')} {...register('discord')} />
                </FormGroup>

                <FormGroup>
                  <label>{t('website.talents.images', 'صور من شغلك')}</label>
                  <input type="file" accept="image/*" multiple {...register('images')} style={{ padding: '6px' }} />
                  <div className="hint">{t('website.talents.images_hint', 'يمكنك رفع حتى 6 صور')}</div>
                </FormGroup>

                <BtnRow>
                  <Btn type="button" variant="reset" onClick={() => reset()} disabled={talents.isLoading}>
                    <FiRotateCcw /> {t('website.talents.reset', 'مسح')}
                  </Btn>
                  <Btn type="submit" disabled={talents.isLoading}>
                    <FiSave /> {talents.isLoading ? t('website.talents.saving', 'جاري الإرسال...') : t('website.talents.submit', 'إرسال الطلب')}
                  </Btn>
                </BtnRow>
              </form>
            </>
          )}
        </Card>
      </Page>
    </WebsiteLayout>
  );
};

export default AddTalentPage;
